import { Component, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges } from "@angular/core";
import { SubjectLocation, SubjectModel } from "../../_objects/subject-model";

interface ImageContext {
  imageSrc: string;
  displayLocations: SubjectLocation[];
  highlightedLocation: SubjectLocation | null;
}

const ImageContext = {
  empty: (): ImageContext => ({
    imageSrc: "",
    displayLocations: [],
    highlightedLocation: null
  })
};

@Component({
  selector: "app-subject-location-image",
  standalone: false,
  templateUrl: "./subject-location-image.component.html",
  styleUrl: "./subject-location-image.component.scss"
})
export class SubjectLocationImageComponent implements OnChanges {
  @Input() subjectModel: SubjectModel;
  @Input() selectedLocation: SubjectLocation;
  @Input() inspectionTitle: string;

  @Output() locationSelected = new EventEmitter<SubjectLocation>();

  imageWidth = 1304;
  imageHeight = 355;
  viewBox = `0 0 ${this.imageWidth} ${this.imageHeight}`;
  structImageSrc: string;
  subjectLocations: SubjectLocation[];
  filterLocation: SubjectLocation;
  isSubjectImageOpen = true;

  constructor(private zone: NgZone) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.subjectModel || changes.selectedLocation) && this.subjectModel) {
      this.updateImageForLocation(this.selectedLocation);
    }
  }

  clickSubjectImageHide(): void {
    if (!this.structImageSrc) {
      return;
    }
    this.isSubjectImageOpen = !this.isSubjectImageOpen;
  }

  onLocationClicked(location: SubjectLocation): void {
    this.locationSelected.emit(location);
  }

  private async updateImageForLocation(location: SubjectLocation | null): Promise<void> {
    const ctx = await this.resolveImageContext(location);
    this.structImageSrc = ctx.imageSrc;
    this.subjectLocations = ctx.displayLocations;
    this.filterLocation = ctx.highlightedLocation;
    if (ctx.imageSrc) {
      this.updateStructImageDimensions(ctx.imageSrc);
      this.isSubjectImageOpen = true;
    } else {
      this.isSubjectImageOpen = false;
    }
  }

  private async resolveImageContext(selectedLocation: SubjectLocation | null): Promise<ImageContext> {
    if (!selectedLocation) {
      return await this.resolveRootImage();
    }

    const path = this.findPathToLocation(this.subjectModel.locations, selectedLocation);
    if (!path) {
      return ImageContext.empty();
    }

    const ancestorImage = await this.resolveNearestAncestorImage(path);
    if (ancestorImage) return ancestorImage;

    const selectedLocationImage = await this.resolveSelectedLocationImage(selectedLocation);
    if (selectedLocationImage) return selectedLocationImage;

    return ImageContext.empty();
  }

  private async resolveRootImage(highlightedLocation: SubjectLocation | null = null): Promise<ImageContext> {
    const imageSrc = this.subjectModel?.structImagePath ?? "";
    return {
      imageSrc,
      displayLocations: this.subjectModel.locations,
      highlightedLocation
    };
  }

  private async resolveNearestAncestorImage(path: SubjectLocation[]): Promise<ImageContext | null> {
    for (let i = path.length - 2; i >= 0; i--) {
      if (path[i].structImagePath) {
        const imageSrc = this.resolveImageSrc(path[i].structImagePath);
        return {
          imageSrc,
          displayLocations: path[i].children,
          highlightedLocation: path[i + 1]
        };
      }
    }

    if (this.subjectModel.structImagePath) {
      return await this.resolveRootImage(path[0]);
    }

    return null;
  }

  private async resolveSelectedLocationImage(selectedLocation: SubjectLocation): Promise<ImageContext | null> {
    if (!selectedLocation.structImagePath) {
      return null;
    }
    const imageSrc = this.resolveImageSrc(selectedLocation.structImagePath);
    return {
      imageSrc,
      displayLocations: selectedLocation.children || [],
      highlightedLocation: null
    };
  }

  private findPathToLocation(locations: SubjectLocation[], target: SubjectLocation, path: SubjectLocation[] = []): SubjectLocation[] | null {
    for (const loc of locations) {
      if (loc.identifier === target.identifier) {
        return [...path, loc];
      }
      if (loc.children?.length > 0) {
        const found = this.findPathToLocation(loc.children, target, [...path, loc]);
        if (found) return found;
      }
    }
    return null;
  }

  private resolveImageSrc(imagePath: string): string {
    return imagePath ?? "";
  }

  private updateStructImageDimensions(imagePath: string): void {
    const image = new Image();

    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;

      if (!width || !height) {
        return;
      }

      this.zone.run(() => {
        this.imageWidth = width;
        this.imageHeight = height;
        this.viewBox = `0 0 ${this.imageWidth} ${this.imageHeight}`;
      });
    };

    image.src = imagePath;
  }
}
